import sanitizeHtml from "sanitize-html";

const allowedTags = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "p",
  "a",
  "ul",
  "ol",
  "nl",
  "li",
  "b",
  "i",
  "strong",
  "em",
  "strike",
  "code",
  "hr",
  "br",
  "div",
  "table",
  "thead",
  "caption",
  "tbody",
  "tr",
  "th",
  "td",
  "pre",
  "iframe",
  "u",
  "s",
  "img",
  "video",
  "span",
];

export const DEFAULT_CONTENT = "<p></p>";

export const sanitiseValue = (value: string) =>
  sanitizeHtml(value, {
    allowedTags,
    allowedAttributes: {
      a: [
        "href",
        "name",
        "rel",
        "target",
        "auto",
        "class",
        "data-mention-atom-id",
        "data-mention-atom-name",
        "style",
      ],
      img: ["src", "width", "height"],
      div: ["style"], // alignment
      span: ["src", "name", "class", "data-ext", "data-node", "style"], // file
      i: ["class"],
      iframe: [
        "src",
        "allowfullscreen",
        "width",
        "height",
        "title",
        "frameborder",
        "allow",
        "referrerpolicy",
      ],
      video: ["src", "allowfullscreen", "width", "height"],
    },
    allowedIframeDomains: ["youtube.com", "vimeo.com", "powerbi.com"],
  });

// `helpers.getText()` can omit atom nodes (e.g. mentions) depending on Remirror/PM internals.
// We compute text from the ProseMirror doc and explicitly provide a leafText callback.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getPlainTextFromDoc = (doc: any) => {
  const size = doc?.content?.size ?? 0;
  if (!doc?.textBetween || size === 0) {
    return "";
  }

  const normalize = (text: string) => {
    const lines = (text || "").replaceAll("\r\n", "\n").split("\n");
    const out: string[] = [];

    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i]?.replace(/[ \t]+$/g, "");
      if (line) {
        out.push(line);
        continue;
      }

      // Keep an empty line only when it's separating two non-empty lines.
      const prevKept = out.at(-1) ?? "";
      if (!prevKept) continue;

      const nextNonEmpty = lines.slice(i + 1).some((l) => l.trim().length > 0);
      if (!nextNonEmpty) continue;

      // Collapse multiple blank lines into one.
      if (out.at(-1) === "") continue;
      out.push("");
    }

    return out.join("\n").trim();
  };

  // `textBetween(from, to, blockSeparator, leafText?)`
  const raw = doc.textBetween(
    0,
    size,
    "\n",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (node: any) => {
      const nodeType = node?.type?.name;

      if (nodeType === "mentionAtom") {
        return (node?.attrs?.label ?? node?.attrs?.id ?? "").toString();
      }

      if (nodeType === "file") {
        return (node?.attrs?.name ?? "").toString();
      }

      return "";
    },
  );

  return normalize(raw);
};
